import { GetServerSidePropsContext } from "next";
import { SWRConfig } from "swr";

import Bugsnag from "@bugsnag/js";
import UAParser from "ua-parser-js";

import ProfilePageLayout, {
  profileUrl as getProfileUrl,
} from "components/ProfilePageLayout";
import ProfilePageAnalysis from "components/ProfilePageLayout/ProfilePageAnalysis";
import {
  profileInsightsUrl,
  sortOptions,
} from "components/ProfilePageLayout/ProfilePageAnalysis/ProfilePageInsightsList";
import { profileReportsUrl } from "components/ProfilePageLayout/ProfilePageAnalysis/ProfilePageReportsList";
import type { NextPageWithLayout } from "pages/_app";
import { baseFetcher } from "utils/baseFetcher";
import { isSsr } from "utils/isSsr";

const ProfileResearchPage: NextPageWithLayout = () => {
  return <ProfilePageAnalysis />;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const slug = context.params?.slug as string;
  const tab = context.query.tab;

  const profileUrl = getProfileUrl(slug);

  const userAgent = context.req.headers["user-agent"] as string;
  const deviceType = new UAParser(userAgent).getDevice().type;
  const isMobile = deviceType === "mobile";
  const session = JSON.parse(
    decodeURIComponent(context.req.cookies["ember_simple_auth-session"] || "{}")
  );
  const authenticated = !!session.authenticated?.token;
  const insightsUrl = profileInsightsUrl(
    slug,
    1,
    {},
    sortOptions[0].sortStr,
    isMobile,
    false,
    authenticated
  );
  const reportsUrl = profileReportsUrl(slug, 1, "", isMobile);
  const publicationsUrl = tab === "reports" ? reportsUrl : insightsUrl;

  try {
    const [profile, publications] = await Promise.all([
      baseFetcher(profileUrl, context),
      isSsr(context)
        ? baseFetcher(publicationsUrl, context)
        : Promise.resolve(null),
    ]);

    if (!profile || profile.errors?.length) return { notFound: true };

    if (!profile.hadInsightsPublished && !profile.hadReportsPublished) {
      return {
        redirect: {
          permanent: false,
          destination: `/profiles/${slug}/about`,
        },
      };
    }

    return {
      props: {
        slug,
        fallback: {
          [profileUrl]: profile,
          [`$inf$${publicationsUrl}`]: [publications],
        },
      },
    };
  } catch (error: any) {
    console.error(
      "[slug].tsx ~ getServerSideProps profile Page ~ error",
      error
    );
    Bugsnag.notify(error);
    return { notFound: true };
  }
}

ProfileResearchPage.getLayout = (page, { pageProps }) => {
  const { fallback } = pageProps;

  return (
    <SWRConfig value={{ fallback }}>
      <ProfilePageLayout {...pageProps}>{page}</ProfilePageLayout>
    </SWRConfig>
  );
};

export default ProfileResearchPage;
